import { lazy, Suspense } from 'react';
import { Route, Switch } from 'react-router-dom';

import DashboardLayout from '@/@mantis/layout/dashboard/DashboardLayout';
import MuiPageWrapper from '@/@mui/MuiPageWrapper';
import { PMPRoutes } from '@/config/routes';
import { MessageNotificationProvider } from '@/context/MessageNotificationProvider';

// import { ChatWidget } from '@/components/chat-widget';
import LoadingState from '@/components/LoadingState';

import AccountPage from '../shared/AccountPage';

import UnitViewTenantListSubpage from './unit-view/UnitViewTenantListSubpage';

const CalendarView = lazy(() => import('@/components/calendar/CalendarView'));
const BuildingListPage = lazy(() => import('./BuildingListPage'));
const BuildingViewPage = lazy(() => import('./BuildingViewPage'));
const ChecklistPage = lazy(() => import('./ChecklistPage'));
const DashboardPage = lazy(() => import('./DashboardPage'));
const DocumentViewPage = lazy(() => import('./DocumentViewPage'));
const UnitViewPage = lazy(() => import('./UnitViewPage'));
const SubscriptionPage = lazy(() => import('./SubscriptionPage'));
const InboundCallListPage = lazy(() => import('./InboundCallListPage'));
const InboundMessagesPage = lazy(() => import('./InboundMessagesPage'));
const InboxPage = lazy(() => import('./InboxPage'));
const TicketListPage = lazy(() => import('./TicketListPage'));
const PropertyLocationListPage = lazy(() => import('./PropertyLocationListPage'));
const PropertyLocationCreatePage = lazy(() => import('./PropertyLocationCreatePage'));
const PropertyLocationViewPage = lazy(() => import('./PropertyLocationViewPage'));
const PropertyLocationEmailCreatePage = lazy(() => import('./PropertyLocationEmailCreatePage'));
const PropertyLocationPhoneCreatePage = lazy(() => import('./PropertyLocationPhoneCreatePage'));
const TenantViewPage = lazy(() => import('./TenantViewPage'));
const SentSMSListPage = lazy(() => import('./SentSMSListPage'));
const SentEmailListPage = lazy(() => import('./SentEmailListPage'));
const WorkflowsPage = lazy(() => import('./WorkflowPage'));
const ConversationListPage = lazy(() => import('./ConversationListPage'));
const ConversationViewPage = lazy(() => import('./ConversationViewPage'));
const InvoiceListPage = lazy(() => import('./InvoiceListPage'));
const OperationsHubWorkflowListPage = lazy(() => import('./OperationsHubWorkflowListPage'));
const VaultPage = lazy(() => import('./operations-hub/VaultPage'));
const ChatWithAIPage = lazy(() => import('./ChatWithAIPage'));
const InviteHouseholdMemberPage = lazy(() => import('./InviteHouseholdMemberPage'));
const ContactListPage = lazy(() => import('./ContactListPage'));
const ContactCreatePage = lazy(() => import('./ContactCreatePage'));
const ContactViewPage = lazy(() => import('./ContactViewPage'));
const ContactEditPage = lazy(() => import('./ContactEditPage'));
const MyCommunitiesPage = lazy(() => import('./MyCommunitiesPage'));
const CommunityViewPage = lazy(() => import('./CommunityViewPage'));
const GroupNegotiationDetailPage = lazy(() => import('./GroupNegotiationDetailPage'));
const VendorSearchPage = lazy(() => import('./VendorSearchPage'));
const VendorSearchResultsPage = lazy(() => import('./VendorSearchResultsPage'));
const VendorSearchHistoryPage = lazy(() => import('./VendorSearchHistoryPage'));
const VendorSearchDetailPage = lazy(() => import('./VendorSearchDetailPage'));

const HomePage: React.FC = () => 
// const location = useLocation();
// const { user } = useAuth();
// const isOnChatWithAIPage = location.pathname === PMPRoutes.chatWithAI;
// const isOnInboxPage = location.pathname.startsWith(PMPRoutes.inbox);
// const isPropertyOwner = user?.user_metadata?.campaign === 'property_owner';

  (
    <MessageNotificationProvider>
      <MuiPageWrapper>
        <DashboardLayout>
          <Suspense fallback={<LoadingState />}>
            <Switch>
              <Route exact path={PMPRoutes.dashboard} component={DashboardPage} />
              <Route exact path={PMPRoutes.calendar} component={CalendarView} />
              <Route exact path={PMPRoutes.buildingList} component={BuildingListPage} />
              <Route exact path={PMPRoutes.documentView} component={DocumentViewPage} />
              <Route
                exact
                path={PMPRoutes.propertyLocationList}
                component={PropertyLocationListPage}
              />

              <Route
                exact
                path={PMPRoutes.propertyLocationCreate}
                component={PropertyLocationCreatePage}
              />

              <Route
                exact
                path={PMPRoutes.propertyLocationEmailCreate}
                component={PropertyLocationEmailCreatePage}
              />

              <Route
                exact
                path={PMPRoutes.propertyLocationPhoneCreate}
                component={PropertyLocationPhoneCreatePage}
              />

              <Route path={PMPRoutes.propertyLocationView} component={PropertyLocationViewPage} />
              <Route
                path={PMPRoutes.propertyLocationViewTenantList}
                component={UnitViewTenantListSubpage}
              />

              <Route path={PMPRoutes.buildingView} component={BuildingViewPage} />
              <Route path={PMPRoutes.unitView} component={UnitViewPage} />
              <Route path={PMPRoutes.tenantView} component={TenantViewPage} />

              <Route path={PMPRoutes.inboundMessages} component={InboundMessagesPage} />
              <Route
                exact
                path={PMPRoutes.inboxConversationView}
                component={ConversationViewPage}
              />
              <Route path={PMPRoutes.inbox} component={InboxPage} />
              <Route path={PMPRoutes.account} component={AccountPage} />

              <Route exact path={PMPRoutes.subscription} component={SubscriptionPage} />
              <Route exact path={PMPRoutes.sentEmailList} component={SentEmailListPage} />
              <Route exact path={PMPRoutes.sentSmsList} component={SentSMSListPage} />
              <Route exact path={PMPRoutes.inboundCallList} component={InboundCallListPage} />
              <Route exact path={PMPRoutes.ticketList} component={TicketListPage} />
              <Route exact path={PMPRoutes.conversationList} component={ConversationListPage} />
              <Route exact path={PMPRoutes.conversationView} component={ConversationViewPage} />
              <Route exact path={PMPRoutes.onboardingChecklist} component={ChecklistPage} />
              <Route exact path={PMPRoutes.workflows} component={WorkflowsPage} />
              <Route
                exact
                path={PMPRoutes.workflowList}
                component={OperationsHubWorkflowListPage}
              />
              <Route exact path={PMPRoutes.invoiceList} component={InvoiceListPage} />
              <Route exact path={PMPRoutes.vault} component={VaultPage} />
              <Route exact path={PMPRoutes.vaultFolder} component={VaultPage} />
              <Route exact path={PMPRoutes.chatWithAI} component={ChatWithAIPage} />
              <Route
                exact
                path={PMPRoutes.inviteHouseholdMember}
                component={InviteHouseholdMemberPage}
              />
              <Route exact path={PMPRoutes.contactList} component={ContactListPage} />
              <Route exact path={PMPRoutes.contactCreate} component={ContactCreatePage} />
              <Route exact path={PMPRoutes.contactView} component={ContactViewPage} />
              <Route exact path={PMPRoutes.contactEdit} component={ContactEditPage} />
              <Route exact path={PMPRoutes.myCommunities} component={MyCommunitiesPage} />
              <Route exact path={PMPRoutes.communityView} component={CommunityViewPage} />
              <Route
                exact
                path={PMPRoutes.groupNegotiationView}
                component={GroupNegotiationDetailPage}
              />
              <Route exact path={PMPRoutes.vendorSearch} component={VendorSearchPage} />
              <Route 
                exact 
                path={PMPRoutes.vendorSearchResults} 
                component={VendorSearchResultsPage} 
              />
              <Route 
                exact 
                path={PMPRoutes.vendorSearchHistory} 
                component={VendorSearchHistoryPage} 
              />
              <Route 
                exact 
                path={PMPRoutes.vendorSearchDetail} 
                component={VendorSearchDetailPage} 
              />
            </Switch>
          </Suspense>

          {/* {!isPropertyOwner && <OnboardingProgressModal />} */}
          {/* {!isOnChatWithAIPage && !isOnInboxPage && <ChatWidget />} */}
        </DashboardLayout>
      </MuiPageWrapper>
    </MessageNotificationProvider>
  )
;

export default HomePage;
